import type { ComponentChildren } from 'preact';

import type {
  Conversation,
  RotationOverviewView,
  ShoppingItem,
  WishList,
} from '@amumin/api-client';
import type { ExtrasGet, PrayerMethod } from '@amumin/domain';
import { inSummerRuleWeeks } from '@amumin/domain/prayer';
import { interpolate, t } from '@amumin/i18n';
import { Button, Notice } from '@amumin/ui';
import { Avatar, Icon } from '@amumin/ui-core';

import { api } from '../api.js';
import { Skeleton, toastError, useLoad } from '../lib/ui.js';
import { appText, household, ownMemberId } from '../state/session.js';
import { wp } from '../weather/text.js';

// Widgets van Vandaag die hun eigen gegevens laden (golf 6b): Boodschappen, Berichten, Wensen en
// Gebedstijden. Elk widget laadt pas als het op het scherm staat, en toont niets zonder inhoud
// (in Aanpassen zegt de lijst dan "Nu niets te zien"). `tick` komt van de live-poke van Vandaag.

/** Rustige kop van een widget met een link naar de pagina erachter. */
export function WidgetHead({
  id,
  title,
  href,
  link,
}: {
  id: string;
  title: string;
  href?: string;
  link?: string;
}) {
  return (
    <div class="section-head">
      <h2 class="section-title" id={id}>
        {title}
      </h2>
      {href && link ? (
        <a href={href} class="small">
          {link}
        </a>
      ) : null}
    </div>
  );
}

export function Panel({
  name,
  labelledBy,
  children,
}: {
  name: string;
  labelledBy: string;
  children: ComponentChildren;
}) {
  return (
    <section class="panel" aria-labelledby={labelledBy} data-name={name}>
      {children}
    </section>
  );
}

// ---- Beurtrooster (huisgenoten) ----------------------------------------------------------------

/**
 * Fixer golf 6b r1 (founder-ux): de kern van een studentenhuis op Vandaag. Jouw beurt deze week
 * (met gedaan of nog niet) en wie er vandaag kookt; daaronder kort de rest van het huis.
 */
export function RotationWidget({ tick }: { tick: number }) {
  const a = appText.value;
  const h = a.home;
  const r = h.rotation;
  const load = useLoad<RotationOverviewView>(() => api.households.rotation.overview(), [tick]);
  const data = load.data.value;
  if (load.error.value && !data) return null;
  const members = household.value?.members ?? [];
  const me = ownMemberId.value;
  const nameOf = (id: string | null) => members.find((x) => x.id === id)?.display_name ?? '';
  const weekly = (data?.duties ?? []).filter((d) => d.cadence === 'week');
  const daily = (data?.duties ?? []).filter((d) => d.cadence === 'day' && d.current.member_id);
  const mine = weekly.filter((d) => me && d.current.member_id === me);
  const others = weekly.filter((d) => !me || d.current.member_id !== me);
  return (
    <Panel name="widget-rotation" labelledBy="w-rotation-title">
      <WidgetHead
        id="w-rotation-title"
        title={h.widgets.rotation.name}
        href="/rooster"
        link={r.all}
      />
      {!data ? (
        <Skeleton rows={2} />
      ) : data.duties.length === 0 ? (
        <p class="muted small">{r.empty}</p>
      ) : (
        <>
          <p class="section-label" data-name="rotation-you-label">
            {r.you}
          </p>
          {mine.length === 0 ? (
            <p class="muted small" data-name="rotation-none">
              {r.noneForYou}
            </p>
          ) : (
            <ul class="rotation-mine" data-name="rotation-mine">
              {mine.map((d) => {
                const done = d.current.done_by_member_id !== null;
                return (
                  <li key={d.item_id} class={done ? 'rotation-duty is-done' : 'rotation-duty'}>
                    {/* Restpunten: geen pictogram per regel in de ouderapp. */}
                    <strong>{d.name}</strong>
                    <span class={done ? 'tag tag-ok' : 'tag'}>
                      {done ? <Icon id="check" size={14} /> : null} {done ? r.done : r.open}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {daily.map((d) => (
            <p key={d.item_id} class="small rotation-today" data-name="rotation-cooks">
              {d.current.member_id === me
                ? r.youCook
                : t(r.cooks, { name: nameOf(d.current.member_id) })}
            </p>
          ))}
          {others.length > 0 ? (
            <ul class="widget-chips" aria-label={r.others} data-name="rotation-others">
              {others.map((d) => (
                <li key={d.item_id} class="widget-chip">
                  <span>
                    {d.name} · {nameOf(d.current.member_id) || '—'}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </Panel>
  );
}

// ---- Boodschappen ------------------------------------------------------------------------------

interface ShoppingData {
  open: number;
  items: ShoppingItem[];
}

export function ShoppingWidget({ tick }: { tick: number }) {
  const a = appText.value;
  const h = a.home;
  const load = useLoad<ShoppingData>(async () => {
    const lists = (await api.app.food.lists.list()).items;
    const open = lists.reduce((n, l) => n + l.open_count, 0);
    const first = lists.find((l) => l.open_count > 0);
    const items = first
      ? (await api.app.food.lists.detail(first.id)).items.filter((i) => !i.checked)
      : [];
    return { open, items };
  }, [tick]);
  const data = load.data.value;
  if (load.error.value && !data) return null;
  return (
    <Panel name="widget-shopping" labelledBy="w-shopping-title">
      <WidgetHead
        id="w-shopping-title"
        title={h.widgets.shopping.name}
        href="/eten/boodschappen"
        link={h.shopping.all}
      />
      {!data ? (
        <Skeleton rows={2} />
      ) : data.open === 0 ? (
        <p class="muted small">{h.shopping.empty}</p>
      ) : (
        <>
          <p class="small" data-name="shopping-count">
            {t(h.shopping.open, { count: data.open })}
          </p>
          <ul class="widget-chips">
            {data.items.slice(0, 6).map((item) => (
              <li key={item.id} class="widget-chip">
                <span>{item.name}</span>
              </li>
            ))}
            {data.open > 6 ? (
              <li class="widget-chip widget-chip-more" data-fit-count={data.open - 6}>
                {t(h.shopping.more, { count: data.open - 6 })}
              </li>
            ) : null}
          </ul>
        </>
      )}
    </Panel>
  );
}

// ---- Berichten ---------------------------------------------------------------------------------

function preview(c: Conversation): string {
  const h = appText.value.home;
  const msg = c.last_message;
  if (!msg || msg.deleted) return '';
  if (msg.body) return msg.body;
  if (msg.voice) return h.messages.voice;
  if (msg.sticker) return h.messages.sticker;
  if (msg.pictos.length > 0) return h.messages.pictos;
  return '';
}

export function MessagesWidget({ tick }: { tick: number }) {
  const h = appText.value.home;
  const load = useLoad(() => api.family.app.conversations(), [tick]);
  const convs = load.data.value?.items ?? null;
  if (load.error.value && !convs) return null;
  const unread = (convs ?? []).filter((c) => c.unread > 0);
  const total = unread.reduce((n, c) => n + c.unread, 0);
  return (
    <Panel name="widget-messages" labelledBy="w-messages-title">
      <WidgetHead
        id="w-messages-title"
        title={h.widgets.messages.name}
        href="/berichten"
        link={h.messages.all}
      />
      {!convs ? (
        <Skeleton rows={2} />
      ) : total === 0 ? (
        <p class="muted small">{h.messages.none}</p>
      ) : (
        <>
          <p class="small">{t(h.messages.unread, { count: total })}</p>
          <ul class="widget-list" data-fit-extra={Math.max(0, unread.length - 3)}>
            {unread.slice(0, 3).map((c) => {
              const who = c.last_message?.sender;
              return (
                <li key={c.id}>
                  <a class="widget-row" href="/berichten">
                    {who ? (
                      <Avatar
                        name={who.name}
                        colorKey={(who.color_key ?? 'blue') as 'blue'}
                        icon={who.avatar_icon}
                        size="small"
                      />
                    ) : (
                      <Icon id="mail" size={20} />
                    )}
                    <span class="widget-row-text">
                      <strong>{c.title}</strong>
                      <span class="muted small block widget-ellipsis">{preview(c)}</span>
                    </span>
                    <span class="widget-badge">{c.unread}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Panel>
  );
}

// ---- Wensen ------------------------------------------------------------------------------------

export function WishesWidget({ tick }: { tick: number }) {
  const h = appText.value.home;
  const load = useLoad(() => api.family.app.wishes(false), [tick]);
  const lists: WishList[] | null = load.data.value?.items ?? null;
  if (load.error.value && !lists) return null;
  const withWishes = (lists ?? []).filter((l) => l.wishes.some((w) => w.status === 'open'));
  return (
    <Panel name="widget-wishes" labelledBy="w-wishes-title">
      <WidgetHead
        id="w-wishes-title"
        title={h.widgets.wishes.name}
        href="/wensen"
        link={h.wishes.all}
      />
      {!lists ? (
        <Skeleton rows={2} />
      ) : withWishes.length === 0 ? (
        <p class="muted small">{h.wishes.empty}</p>
      ) : (
        <ul class="widget-list" data-fit-extra={Math.max(0, withWishes.length - 4)}>
          {withWishes.slice(0, 4).map((l) => {
            const open = l.wishes.filter((w) => w.status === 'open');
            return (
              <li key={l.id} class="widget-row">
                <Avatar
                  name={l.member.display_name}
                  colorKey={l.member.color_key}
                  icon={l.member.avatar_icon}
                  size="small"
                />
                <span class="widget-row-text">
                  <strong>{l.member.display_name}</strong>
                  <span class="muted small block widget-ellipsis">
                    {open
                      .slice(0, 3)
                      .map((w) => w.title)
                      .join(', ')}
                  </span>
                </span>
                <span class="muted small">{t(h.wishes.count, { count: open.length })}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

// ---- Gebedstijden ------------------------------------------------------------------------------

const PRAYER_KEYS = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'] as const;
type PrayerKey = (typeof PRAYER_KEYS)[number];

export interface PrayerToday {
  date: string;
  times: Record<PrayerKey, string>;
  naming: 'turkish' | 'arabic';
  fromTable: boolean;
  method: PrayerMethod;
  /** Eenmalige melding na het overzetten naar de Europese kalender (0041), nog niet gezien. */
  notice: boolean;
  /**
   * Imsak (begin van het vasten) als kleine regel boven de tegels, na de afwijking van Fajr; alleen
   * in de Ramadan, zoals de Ramadan-kaart van de kiosk (oprichter 27-09); null = geen regel.
   */
  imsak: string | null;
}

function addMinutes(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${hh < 10 ? '0' : ''}${hh}:${mm < 10 ? '0' : ''}${mm}`;
}

/**
 * De tijden van vandaag zoals het gezin ze ziet: uit het rooster van de moskee zoals ze zijn,
 * anders de berekening plus de afwijkingen van deze maand (zelfde regel als Instellingen · Extra).
 * null = geen gebedstijden (niet aangeboden, niet gekozen of uit).
 */
export function prayerTodayOf(res: ExtrasGet | null | undefined): PrayerToday | null {
  const p = res?.prayer;
  if (!p || !p.configured || !p.settings.enabled || !p.today) return null;
  const today = p.today;
  const fromTable = today.source === 'mosque_table';
  const month = String(Number(today.date.slice(5, 7)));
  const plan = p.settings.offsets;
  const offsets = (plan.months as Record<string, typeof plan.base | undefined>)[month] ?? plan.base;
  const times = {} as Record<PrayerKey, string>;
  for (const key of PRAYER_KEYS) {
    times[key] = fromTable ? today.times[key] : addMinutes(today.times[key], offsets[key]);
  }
  return {
    date: today.date,
    times,
    naming: p.settings.naming,
    fromTable,
    method: p.settings.method,
    notice: p.settings.method_notice === 'eu_joint_moved',
    imsak: today.imsak ? (fromTable ? today.imsak : addMinutes(today.imsak, offsets.fajr)) : null,
  };
}

export function PrayerWidget({
  prayer,
  nowHhmm,
  onSeen,
}: {
  prayer: PrayerToday | null;
  nowHhmm: string;
  /** Na "Oké" op de eenmalige melding: opnieuw laden. */
  onSeen?: () => void;
}) {
  const h = appText.value.home;
  if (!prayer) return null;
  const w = wp.value.prayer;
  const names = w.names[prayer.naming];
  // Review golf 6b (regressie r1): de melding over de Europese kalender hier, waar het gezin de
  // tijden ziet, en niet alleen in Instellingen. Eén zin en "Oké"; daarna nooit meer.
  const seen = () =>
    void api.app.extras
      .seen()
      .then(() => onSeen?.())
      .catch(toastError);
  const next = PRAYER_KEYS.find((k) => prayer.times[k] > nowHhmm) ?? null;
  const nextAt = next ? PRAYER_KEYS.indexOf(next) : PRAYER_KEYS.length;
  return (
    <Panel name="widget-prayer" labelledBy="w-prayer-title">
      <WidgetHead
        id="w-prayer-title"
        title={h.widgets.prayer.name}
        href="/instellingen/gezin/extra"
        link={h.prayer.settings}
      />
      {/* Imsak alleen in de Ramadan (de API geeft hem anders niet), als kleine regel boven de tegels
          zoals de Ramadan-kaart van de kiosk: de kaart houdt zes tegels op één rij (oprichter 27-09). */}
      {prayer.imsak ? (
        <p class="prayer-imsak small" data-prayer="imsak" data-name="prayer-imsak">
          <span class="prayer-name">{w.ramadan.imsak[prayer.naming]}</span>{' '}
          <span class="prayer-imsak-hhmm tabular">{prayer.imsak}</span>
        </p>
      ) : null}
      <ol class="prayer-times" data-name="prayer-times">
        {PRAYER_KEYS.map((k, i) => (
          // Past de kaart niet alles (geen scroll in kaarten): eerst de tijden die voorbij zijn weg,
          // de volgende en die daarna blijven altijd staan.
          <li
            key={k}
            data-fit-early={i < nextAt ? '' : undefined}
            data-fit={i < nextAt ? undefined : 'keep'}
            class={k === next ? 'prayer-time is-next' : 'prayer-time'}
            data-prayer={k}
            aria-current={k === next ? 'time' : undefined}
          >
            <span class="prayer-name">{names[k]}</span>
            <span class="prayer-hhmm tabular">{prayer.times[k]}</span>
            {k === next ? <span class="prayer-next">{h.prayer.next}</span> : null}
          </li>
        ))}
      </ol>
      {prayer.fromTable ? <p class="muted small">{h.prayer.fromTable}</p> : null}
      {!prayer.fromTable && !prayer.notice && inSummerRuleWeeks(prayer.method, prayer.date) ? (
        <p class="muted small" data-name="prayer-tune-hint">
          {w.methodTune}{' '}
          <a href="/instellingen/gezin/extra" data-name="prayer-tune-link">
            {h.prayer.settings}
          </a>
        </p>
      ) : null}
      {prayer.notice ? (
        <Notice kind="info" name="today-method-notice">
          <span>{interpolate(w.methodNotice, { fajr: names.fajr, isha: names.isha })} </span>
          <Button name="today-method-notice-ok" variant="link" onPress={seen}>
            {w.methodNoticeOk}
          </Button>
        </Notice>
      ) : null}
    </Panel>
  );
}
